import React from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { Phone, MessageCircle, Clock, MapPin, ChevronRight } from "lucide-react";
import { CITIES } from "../data/cities";
import { useSEO } from "../lib/useSEO";
import { BreadcrumbSchema, FAQSchema } from "../components/SEOSchema";
import { ServiceTerms } from "../components/ServiceTerms";
import { SERVICE_TERMS } from "../data/serviceTerms";

import { SITE_URL } from "../lib/siteConfig";
import { HERO_IMAGE } from "../lib/siteImages";
const PHONE = "+33623060308";
const PHONE_DISPLAY = "06 23 06 03 08";
const WHATSAPP = "https://wa.me/33623060308";

// Service area, not an invented physical business location.
const CityBusinessSchema = ({ city }) => {
  const data = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE_URL}${city.path}#service`,
    name: `Noctua Riviera — Livraison ${city.nameFull}`,
    image: HERO_IMAGE,
    url: `${SITE_URL}${city.path}`,
    provider: { "@id": `${SITE_URL}/#business` },
    description: city.seoDescription,
    areaServed: { "@type": "City", name: city.nameFull },
    hoursAvailable: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        opens: "00:00",
        closes: "23:59",
      },
    ],
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
};

const CityLanding = () => {
  const { pathname } = useLocation();
  const citySlug = pathname.replace(/^\/livraison-/, "").replace(/\/$/, "");
  const city = CITIES[citySlug];

  useSEO({
    title: city?.seoTitle || "Livraison de nuit — Noctua Riviera",
    description: city?.seoDescription || "",
    path: city?.path || "/",
  });

  if (!city) return <Navigate to="/livraison" replace />;

  const cityFaq = [
    ...city.faq,
    { q: "Quel est le minimum de commande et le prix de livraison ?", a: SERVICE_TERMS.minimumAnswer },
    { q: "Quels sont les horaires de restauration ?", a: SERVICE_TERMS.hoursAnswer },
  ];
  const breadcrumb = [
    { name: "Accueil", path: "/" },
    { name: "Livraison", path: "/livraison" },
    { name: city.nameFull, path: city.path },
  ];

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white" data-testid={`city-${city.slug}`}>
      <BreadcrumbSchema trail={breadcrumb} />
      <CityBusinessSchema city={city} />
      <FAQSchema items={cityFaq} />

      {/* HEADER */}
      <header className="border-b border-white/5 py-5">
        <div className="container-x flex items-center justify-between gap-4">
          <Link to="/" className="tracking-[0.35em] text-sm font-display shrink-0">
            NOCTUA <span className="text-[#d4af37] italic">Riviera</span>
          </Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 border border-[#d4af37]/60 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0a0f1a] transition-colors text-xs uppercase tracking-[0.2em]"
              data-testid="city-header-full-menu"
            >
              Menu complet
            </Link>
            <Link
              to="/"
              className="sm:hidden text-[#d4af37] text-xs uppercase tracking-[0.15em] underline underline-offset-4 decoration-[#d4af37]/40"
              data-testid="city-header-full-menu-mobile"
            >
              Menu
            </Link>
            <a
              href={`tel:${PHONE}`}
              className="text-[#d4af37] text-sm tracking-[0.15em] font-semibold hover:underline whitespace-nowrap"
              data-testid="city-header-call"
            >
              <Phone size={14} className="inline mr-2" />
              <span className="hidden sm:inline">{PHONE_DISPLAY}</span>
              <span className="sm:hidden">Appeler</span>
            </a>
          </div>
        </div>
      </header>

      {/* BREADCRUMB */}
      <nav className="container-x py-6 text-xs uppercase tracking-[0.2em] text-gray-500" aria-label="Fil d'Ariane">
        <Link to="/" className="hover:text-white">Accueil</Link>
        <ChevronRight size={12} className="inline mx-2" />
        <Link to="/livraison" className="hover:text-white">Livraison</Link>
        <ChevronRight size={12} className="inline mx-2" />
        <span className="text-[#d4af37]">{city.nameFull}</span>
      </nav>

      {/* HERO */}
      <section className="container-x pb-16 pt-4">
        <p className="eyebrow mb-4 text-[#d4af37]">Livraison de nuit · {city.heroTagline}</p>
        <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl text-white leading-[1.05] mb-6 max-w-4xl">
          Livraison de boissons, snacks et épicerie à <em className="text-[#d4af37]">{city.nameFull}</em>
        </h1>
        <p className="text-lg text-gray-300 leading-relaxed max-w-3xl mb-10">{city.lede}</p>
        <div className="flex flex-wrap gap-4 mb-10">
          <a
            href={`tel:${PHONE}`}
            className="inline-flex items-center gap-3 bg-[#d4af37] text-[#0a0f1a] px-8 py-4 font-semibold tracking-[0.15em] text-sm uppercase hover:bg-white transition-colors"
            data-testid="city-hero-call"
          >
            <Phone size={16} />
            Commander · {PHONE_DISPLAY}
          </a>
          <a
            href={WHATSAPP}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-3 border border-[#d4af37]/50 text-[#d4af37] px-8 py-4 font-semibold tracking-[0.15em] text-sm uppercase hover:bg-[#d4af37] hover:text-[#0a0f1a] transition-colors"
            data-testid="city-hero-whatsapp"
          >
            <MessageCircle size={16} />
            WhatsApp
          </a>
        </div>
        <div className="flex flex-wrap gap-8 text-xs uppercase tracking-[0.2em] text-gray-400">
          <span><Clock size={12} className="inline mr-2 text-[#d4af37]" />{city.eta}</span>
          <span><MapPin size={12} className="inline mr-2 text-[#d4af37]" />Tous quartiers</span>
          <span data-testid="city-payment-methods">Espèces · Carte bancaire · Lien de paiement</span>
          <span>FR · EN</span>
        </div>
        <ServiceTerms testId="city-service-terms" className="mt-6" />
      </section>

      {/* NEIGHBORHOODS */}
      <section className="border-t border-white/5 py-16 sm:py-20 bg-[#0e1524]">
        <div className="container-x">
          <p className="eyebrow mb-3">Zones couvertes</p>
          <h2 className="font-display text-3xl sm:text-4xl text-white mb-10">
            Tous les quartiers de <em className="text-[#d4af37]">{city.nameFull}</em>
          </h2>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {city.neighborhoods.map((n, i) => (
              <li key={i} className="border border-white/8 p-6 hover:border-[#d4af37]/40 transition-colors">
                <p className="text-[#d4af37] text-xs tracking-[0.2em] uppercase mb-3">{n.name}</p>
                <p className="text-gray-300 text-sm leading-relaxed">{n.note}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* LANDMARKS */}
      <section className="border-t border-white/5 py-16 sm:py-20">
        <div className="container-x max-w-4xl">
          <p className="eyebrow mb-3">Livraisons habituelles</p>
          <h2 className="font-display text-3xl sm:text-4xl text-white mb-10">
            Hôtels, ports et lieux d'intérêt à <em className="text-[#d4af37]">{city.nameFull}</em>
          </h2>
          <ul className="space-y-4">
            {city.landmarks.map((l, i) => (
              <li key={i} className="flex gap-4 text-gray-200 leading-relaxed">
                <span className="text-[#d4af37] mt-1.5 shrink-0">◆</span>
                <span>{l}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* MENU LINKS — internal linking boost */}
      <section className="border-t border-white/5 py-16 sm:py-20 bg-[#0e1524]">
        <div className="container-x">
          <p className="eyebrow mb-3">Notre carte</p>
          <h2 className="font-display text-3xl sm:text-4xl text-white mb-10">
            Que livrons-nous à <em className="text-[#d4af37]">{city.nameFull}</em> ?
          </h2>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { path: "/shop/restauration", name: "Pizzas & Restauration", desc: "Pizzas napolitaines, pâtes, desserts" },
              { path: "/shop/planches", name: "Plateaux gourmands", desc: "Charcuterie fine, fromages affinés" },
              { path: "/shop/soft_drinks", name: "Boissons", desc: "Sodas, énergisants, eaux, jus" },
              { path: "/shop/snacks", name: "Snacks", desc: "Pringles, Haribo, Oreo, Kinder" },
              { path: "/shop/alcohol", name: "Alcool 18+", desc: "Champagne, spiritueux, vins" },
              { path: "/shop/tobacco", name: "Tabac 18+", desc: "Marlboro, Winston, Camel, Philip Morris" },
            ].map((cat) => (
              <li key={cat.path}>
                <Link
                  to={cat.path}
                  className="block border border-white/8 p-6 hover:border-[#d4af37] transition-colors group"
                >
                  <p className="text-[#d4af37] text-xs tracking-[0.2em] uppercase mb-3 flex items-center justify-between">
                    {cat.name}
                    <ChevronRight size={14} className="opacity-40 group-hover:opacity-100 transition-opacity" />
                  </p>
                  <p className="text-gray-300 text-sm">{cat.desc}</p>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <div className="container-x py-6">
        <Link to="/livraison-alcool-24h24" className="text-sm text-[#d4af37] underline underline-offset-4 hover:text-white transition-colors" data-testid="city-alcohol-day-night-link">Livraison d’alcool en journée et la nuit : consultez nos conditions 24h/24 →</Link>
      </div>

      <section className="border-t border-white/5 py-16 sm:py-20">
        <div className="container-x max-w-4xl">
          <p className="eyebrow mb-3">Questions fréquentes</p>
          <h2 className="font-display text-3xl sm:text-4xl text-white mb-10">
            Livraison à <em className="text-[#d4af37]">{city.nameFull}</em> — vos questions
          </h2>
          <ul className="space-y-8">
            {cityFaq.map((f, i) => (
              <li key={i} className="border-l-2 border-[#d4af37]/60 pl-6" data-testid={`city-faq-${i}`}>
                <p className="text-white font-semibold mb-3 text-lg">{f.q}</p>
                <p className="text-gray-300 leading-relaxed">{f.a}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CROSS-LINKS to other cities */}
      <section className="border-t border-white/5 py-16 bg-[#0e1524]">
        <div className="container-x">
          <p className="eyebrow mb-3">Autres villes desservies</p>
          <h2 className="font-display text-2xl sm:text-3xl text-white mb-8">
            De <em className="text-[#d4af37]">Cannes à Monaco</em>
          </h2>
          <ul className="flex flex-wrap gap-3">
            {Object.values(CITIES)
              .filter((c) => c.slug !== city.slug)
              .map((c) => (
                <li key={c.slug}>
                  <Link
                    to={c.path}
                    className="inline-block border border-white/12 px-5 py-3 text-sm hover:border-[#d4af37] hover:text-[#d4af37] transition-colors"
                    data-testid={`link-city-${c.slug}`}
                  >
                    Livraison à {c.nameFull} →
                  </Link>
                </li>
              ))}
          </ul>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-white/5 py-20 text-center">
        <div className="container-x max-w-3xl">
          <p className="eyebrow mb-4 text-[#d4af37]">Prêt à commander ?</p>
          <h2 className="font-display text-3xl sm:text-5xl text-white mb-6 leading-tight">
            La nuit à {city.nameFull} <em className="text-[#d4af37]">vous appartient.</em>
          </h2>
          <p className="text-gray-300 text-lg mb-10">
            Un appel, {city.eta.split(" ")[0]} minutes plus tard, tout est chez vous.
          </p>
          <a
            href={`tel:${PHONE}`}
            className="inline-flex items-center gap-3 bg-[#d4af37] text-[#0a0f1a] px-10 py-5 font-semibold tracking-[0.2em] text-sm uppercase hover:bg-white transition-colors"
            data-testid="city-final-call"
          >
            <Phone size={18} />
            {PHONE_DISPLAY}
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/5 py-10">
        <div className="container-x text-center text-xs uppercase tracking-[0.25em] text-gray-500">
          © {new Date().getFullYear()} Noctua Riviera — Tous droits réservés
        </div>
      </footer>
    </div>
  );
};

export default CityLanding;
